import React from 'react';

/**
 * MiniFloorplanMap — A compact visual representation of the banquet hall grid
 * with the guest's assigned slot highlighted (pulsing green dot).
 *
 * Grid structure (from project overview):
 * - 2 sections: L (Left) and R (Right), separated by a walkway
 * - 4 Row-Sets: 1 (Front/Stage), 2 (Middle), 3 (Upper-Middle), 4 (Back/Entrance)
 * - Row-Sets 1-3 have 2 rows each (A, B); Row-Set 4 has 1 row
 * - 3 Columns per side: W (Wall), C (Center), A (Aisle) — each with 2 sub-positions (1, 2)
 * - Row-Set 1 has limited Aisle positions (dance floor area)
 *
 * Grid Address Format: {Section}-{RowSet}{Position}-{Column}{Index}
 * e.g., L-2B-W1, R-4-A2
 */

interface MiniFloorplanMapProps {
  /** The highlighted grid address, e.g. "L-2B-W1" */
  highlightedGridAddress: string | null | undefined;
  /** Suggested entrance name for door indicator */
  suggestedEntrance?: string | null;
}

// Generate all 94 grid addresses (80 original + 14 W3 expansion)
function generateAllSlots(): string[] {
  const slots: string[] = [];
  const sections = ['L', 'R'] as const;
  const columns = ['W', 'C', 'A'] as const;
  const indices = ['1', '2'] as const;

  for (const section of sections) {
    // Row-Sets 1-3: rows A and B
    for (const rs of ['1', '2', '3']) {
      for (const pos of ['A', 'B']) {
        for (const col of columns) {
          if (rs === '1' && col === 'A') {
            // Row-Set 1 limited Aisle: Left gets A1, Right gets A2
            if (section === 'L') slots.push(`${section}-${rs}${pos}-A1`);
            else slots.push(`${section}-${rs}${pos}-A2`);
            continue;
          }
          for (const idx of indices) {
            slots.push(`${section}-${rs}${pos}-${col}${idx}`);
          }
        }
        // W3 slot for every row position on both sides
        slots.push(`${section}-${rs}${pos}-W3`);
      }
    }
    // Row-Set 4: single row (no A/B)
    for (const col of columns) {
      for (const idx of indices) {
        slots.push(`${section}-4-${col}${idx}`);
      }
    }
    // W3 for Row-Set 4
    slots.push(`${section}-4-W3`);
  }
  return slots;
}

const ALL_SLOTS = generateAllSlots();

// Map grid address to x,y position on a normalized coordinate system
function getSlotPosition(address: string): { x: number; y: number } | null {
  // Parse: Section-RowSetPos-ColIdx
  const match = address.match(/^([LR])-(\d)([AB]?)-([WCA])(\d)$/);
  if (!match) return null;

  const [, section, rowSetStr, rowPos, column, indexStr] = match;
  const rowSet = parseInt(rowSetStr);
  const index = parseInt(indexStr);

  // X axis: section + column + index
  // Left section:  W3=0, W1=1, W2=2, C1=3, C2=4, A1=5, A2=6
  // Right section: A1=8, A2=9, C1=10, C2=11, W1=12, W2=13, W3=14
  // (gap of 1 unit in the middle for walkway at position 7)

  let x: number;
  if (section === 'L') {
    if (column === 'W' && index === 3) {
      x = 0;
    } else {
      const colOrder: Record<string, number> = { W: 1, C: 3, A: 5 };
      x = colOrder[column] + (index - 1);
    }
  } else {
    if (column === 'W' && index === 3) {
      x = 14;
    } else {
      // Right side mirrors: A is closest to walkway
      const rColOrder: Record<string, number> = { A: 8, C: 10, W: 12 };
      x = rColOrder[column] + (index - 1);
    }
  }

  // Y axis: row-set + position (top = stage = row-set 1, bottom = entrance = row-set 4)
  // Row-set 1: rows A,B → y = 0, 1
  // Row-set 2: rows A,B → y = 2, 3
  // Row-set 3: rows A,B → y = 4, 5
  // Row-set 4: single → y = 6
  let y: number;
  if (rowSet === 4) {
    y = 6;
  } else {
    const baseY = (rowSet - 1) * 2;
    y = baseY + (rowPos === 'B' ? 1 : 0);
  }

  return { x, y };
}

// Door positions along the bottom
const DOORS = [
  { label: 'A', x: 0.5 },
  { label: 'B', x: 3.5 },
  { label: 'M', x: 7 },
  { label: 'D', x: 10.5 },
  { label: 'E', x: 14 },
];

function matchDoorLabel(suggested: string): string | null {
  if (!suggested) return null;
  const lower = suggested.toLowerCase().trim();
  if (lower.includes('salon a') || lower === 'a') return 'A';
  if (lower.includes('salon b') || lower === 'b') return 'B';
  if (lower.includes('main') || lower.includes('center')) return 'M';
  if (lower.includes('salon d') || lower === 'd') return 'D';
  if (lower.includes('salon e') || lower === 'e') return 'E';
  return null;
}

export function MiniFloorplanMap({ highlightedGridAddress, suggestedEntrance }: MiniFloorplanMapProps) {
  const highlightedDoor = matchDoorLabel(suggestedEntrance || '');

  // Dimensions
  const GRID_COLS = 15; // 0-14
  const GRID_ROWS = 7; // 0-6
  const CELL_SIZE = 16;
  const PAD_X = 12;
  const PAD_Y = 24; // top padding for "STAGE" label
  const PAD_BOTTOM = 32; // bottom for doors
  const WIDTH = GRID_COLS * CELL_SIZE + PAD_X * 2;
  const HEIGHT = GRID_ROWS * CELL_SIZE + PAD_Y + PAD_BOTTOM;

  return (
    <div style={{ display: 'inline-block', backgroundColor: '#1a1a2e', borderRadius: '10px', padding: '8px', position: 'relative' }}>
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        style={{ display: 'block' }}
        aria-label="Mini floorplan showing your seat location"
      >
        {/* Stage label */}
        <text x={WIDTH / 2} y={14} textAnchor="middle" fontSize="8" fill="#aaa" fontWeight="600" fontFamily="system-ui">
          STAGE
        </text>

        {/* Walkway line */}
        <line
          x1={PAD_X + 7 * CELL_SIZE + CELL_SIZE / 2}
          y1={PAD_Y - 4}
          x2={PAD_X + 7 * CELL_SIZE + CELL_SIZE / 2}
          y2={PAD_Y + GRID_ROWS * CELL_SIZE + 2}
          stroke="#444"
          strokeWidth="1"
          strokeDasharray="3,2"
        />

        {/* Section labels */}
        <text x={PAD_X + 3 * CELL_SIZE} y={PAD_Y - 4} textAnchor="middle" fontSize="7" fill="#888" fontFamily="system-ui">LEFT</text>
        <text x={PAD_X + 11 * CELL_SIZE} y={PAD_Y - 4} textAnchor="middle" fontSize="7" fill="#888" fontFamily="system-ui">RIGHT</text>

        {/* Virtual Dance Floor — occupies the aisle gap in Row-Set 1 */}
        {(() => {
          // Dance floor spans: x from L-A2 (6) to R-A1 (9), y from row 0 to row 1 (Row-Set 1)
          const dfX = PAD_X + 6 * CELL_SIZE;
          const dfY = PAD_Y;
          const dfW = 3 * CELL_SIZE; // covers positions 6, 7 (walkway), 8
          const dfH = 2 * CELL_SIZE; // 2 rows of Row-Set 1
          return (
            <g>
              <rect
                x={dfX}
                y={dfY - 2}
                width={dfW}
                height={dfH + 4}
                rx="4"
                fill="none"
                stroke="#f59e0b"
                strokeWidth="1"
                strokeDasharray="3,2"
                opacity="0.7"
              />
              <text x={dfX + dfW / 2} y={dfY + dfH / 2 + 2} textAnchor="middle" fontSize="6" fill="#f59e0b" fontWeight="600" fontFamily="system-ui" opacity="0.9">
                DANCE FLOOR
              </text>
            </g>
          );
        })()}

        {/* All slots */}
        {ALL_SLOTS.map((address) => {
          const pos = getSlotPosition(address);
          if (!pos) return null;
          const cx = PAD_X + pos.x * CELL_SIZE + CELL_SIZE / 2;
          const cy = PAD_Y + pos.y * CELL_SIZE + CELL_SIZE / 2;
          const isHighlighted = address === highlightedGridAddress;

          if (isHighlighted) {
            return (
              <g key={address}>
                {/* Pulsing ring */}
                <circle cx={cx} cy={cy} r="7" fill="none" stroke="#4ade80" strokeWidth="1.5" opacity="0.6">
                  <animate attributeName="r" values="5;9;5" dur="1.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.8;0.2;0.8" dur="1.5s" repeatCount="indefinite" />
                </circle>
                {/* Main dot */}
                <circle cx={cx} cy={cy} r="5" fill="#22c55e" stroke="#fff" strokeWidth="1.5" />
                {/* "YOU" label */}
                <text x={cx} y={cy - 9} textAnchor="middle" fontSize="6" fill="#4ade80" fontWeight="700" fontFamily="system-ui">YOU</text>
              </g>
            );
          }

          return (
            <circle
              key={address}
              cx={cx}
              cy={cy}
              r="3"
              fill="#3a3a5c"
              stroke="#555"
              strokeWidth="0.5"
            />
          );
        })}

        {/* Doors along bottom — double door icons */}
        {DOORS.map((door) => {
          const dx = PAD_X + door.x * CELL_SIZE;
          const dy = PAD_Y + GRID_ROWS * CELL_SIZE + 16;
          const isActive = door.label === highlightedDoor;
          const isMain = door.label === 'M';
          const doorWidth = isMain ? 24 : 14;
          const panelGap = 1.5;
          const panelW = isMain ? (doorWidth - panelGap * 3) / 4 : (doorWidth - panelGap) / 2;
          const panelH = 10;
          const fill = isActive ? '#1a6b3c' : '#2a2a4a';
          const stroke = isActive ? '#4ade80' : '#555';

          return (
            <g key={door.label}>
              {/* Door panels */}
              {isMain ? (
                // Main entrance: 4 panels (double-double door)
                <>
                  {[0, 1, 2, 3].map((i) => (
                    <rect
                      key={i}
                      x={dx - doorWidth / 2 + i * (panelW + panelGap)}
                      y={dy - panelH / 2}
                      width={panelW}
                      height={panelH}
                      rx="1.5"
                      fill={fill}
                      stroke={stroke}
                      strokeWidth={isActive ? 1.2 : 0.5}
                    />
                  ))}
                </>
              ) : (
                // Salon doors: 2 panels (double door)
                <>
                  <rect
                    x={dx - doorWidth / 2}
                    y={dy - panelH / 2}
                    width={panelW}
                    height={panelH}
                    rx="1.5"
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={isActive ? 1.2 : 0.5}
                  />
                  <rect
                    x={dx - doorWidth / 2 + panelW + panelGap}
                    y={dy - panelH / 2}
                    width={panelW}
                    height={panelH}
                    rx="1.5"
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={isActive ? 1.2 : 0.5}
                  />
                </>
              )}
              {/* Label below */}
              <text x={dx} y={dy + panelH / 2 + 8} textAnchor="middle" fontSize="5.5" fill={isActive ? '#4ade80' : '#999'} fontWeight={isActive ? '700' : '400'} fontFamily="system-ui">
                {door.label}
              </text>
              {/* Arrow above highlighted door */}
              {isActive && (
                <polygon
                  points={`${dx - 3},${dy - panelH / 2 - 5} ${dx + 3},${dy - panelH / 2 - 5} ${dx},${dy - panelH / 2 - 1}`}
                  fill="#4ade80"
                />
              )}
            </g>
          );
        })}

        {/* "ENTRANCE" label */}
        <text x={WIDTH / 2} y={HEIGHT - 4} textAnchor="middle" fontSize="7" fill="#888" fontFamily="system-ui">
          ENTRANCE
        </text>
      </svg>
    </div>
  );
}
